/**
 * Per-agent state shared across manager-worker subsystems.
 *
 * ADR-0011 PR-A: the agentState namespace consolidates the multi-consumer
 * caches that previously lived as module-level `const` Maps inside
 * manager-worker.ts. The intent is purely structural — no behavior changes,
 * no renames of keys or value shapes. Subsystem-owned state (kanban harvest
 * caches, scheduler in-flight Sets, memory hash maps, etc.) does NOT live
 * here; it lifts with its subsystem in the later stacked PRs (PR-B onwards).
 *
 * See docs/adr/0011-decompose-manager-worker.md for the full rationale,
 * including the rejection of an `AgentStateStore` class wrapper.
 */

/**
 * The shared per-agent caches. Keys are documented per field:
 * - `agentId` — host_agents.agent_id (uuid). Stable across rename.
 * - `codeName` — agents.code_name (kebab-case slug). Surfaces in tmux session
 *   names, log paths, and CLI prompts.
 * - `agentId:<suffix>` — compound keys whose suffix identifies a sub-resource
 *   (e.g. a channel binding or skill file).
 */
export const agentState = {
  // ---------------------------------------------------------------------------
  // agentId-keyed
  // ---------------------------------------------------------------------------
  knownVersions: new Map<string, { charterVersion: string; toolsVersion: string }>(),
  knownStatuses: new Map<string, string>(),
  knownChannels: new Map<string, Set<string>>(),
  writtenHashes: new Map<string, Map<string, string>>(),
  knownSecretsHashes: new Map<string, string>(),
  knownModels: new Map<string, string>(),
  knownTasksHashes: new Map<string, string>(),
  knownIntegrationHashes: new Map<string, string>(),
  knownManagedMcpHashes: new Map<string, string>(),
  knownManagedMcpStructure: new Map<string, string>(),
  /**
   * ENG-5858: hash of the effective sender_policy returned by
   * /host/refresh for this agent. Tracked separately from
   * `knownChannelConfigHashes` because the channel-cred hash is
   * per-channel (cleared/rewritten per channel) whereas sender_policy is
   * agent-wide. A change here triggers a session restart so the
   * slack-channel / teams-channel MCP children pick up the new env vars
   * (they read SLACK_SENDER_POLICY / MSTEAMS_SENDER_POLICY only at boot).
   */
  knownSenderPolicyHashes: new Map<string, string>(),
  /**
   * ENG-6024: hash of the Teams behaviour subset (thread_auto_follow,
   * channel_response_mode, adaptive_cards_enabled/_ask_user_enabled) from the
   * agent's msteams channel config. Same last-hop rationale as
   * `knownSenderPolicyHashes`: teams-channel reads the corresponding env vars
   * once at boot, and neither the channel-set restart nor the .mcp.json drift
   * watcher fires on a value-only change, so this dedicated trigger restarts
   * the session when an operator saves the webapp's Teams Advanced panel.
   */
  knownMsTeamsBehaviourHashes: new Map<string, string>(),
  /**
   * ENG-6035: hash of the Slack behaviour subset (allowed_users,
   * thread_auto_follow, channel_response_mode) from the agent's slack
   * channel config. Same last-hop rationale as the Teams map above —
   * slack-channel reads SLACK_ALLOWED_USERS (and friends) once at boot,
   * so an operator save needs this dedicated value-change trigger.
   */
  knownSlackBehaviourHashes: new Map<string, string>(),

  // ---------------------------------------------------------------------------
  // codeName-keyed
  // ---------------------------------------------------------------------------
  agentDisplayNames: new Map<string, string>(),
  codeNameToAgentId: new Map<string, string>(),
  /**
   * Set of code_names with a live persistent session managed by this worker.
   * Borderline ownership: written from pollCycle and the persistent-session
   * lifecycle, read from the kanban-inject gate. Lives in agentState to keep
   * the kanban subsystem from importing from lifecycle in PR-C/E.
   */
  persistentSessionAgents: new Set<string>(),

  // ---------------------------------------------------------------------------
  // compound-keyed (`agentId:<suffix>`)
  // ---------------------------------------------------------------------------
  knownChannelConfigHashes: new Map<string, string>(),
  knownSkillHashes: new Map<string, string>(),

  // ---------------------------------------------------------------------------
  // agentId-keyed (global skills, ENG-6349)
  // ---------------------------------------------------------------------------
  /**
   * ENG-6349 (ADR-0023): per-agent set of global-skill ids (bare code_names)
   * this worker installed on the previous refresh. Global skills have no folder
   * prefix (they're written as `.claude/skills/<code_name>/` so `/<code_name>`
   * resolves), so the integration orphan sweep — which scans for `integration-`/
   * `plugin-` prefixes — can't see them. We track the delivered set here instead
   * and remove only the ids that drop out (an unpublished global), never the
   * agent's own or integration skills. In-memory only: a manager restart resets
   * it, so an unpublish during a restart window leaves the folder until the next
   * unpublish — acceptable for a platform-staff-rare action.
   */
  knownGlobalSkillIds: new Map<string, Set<string>>(),
} as const;

/**
 * Clear the per-agent state owned by `agentState` for one agent.
 *
 * Returns `true` if any compound `knownChannelConfigHashes` entry was deleted,
 * so the caller can persist the on-disk channel-hash cache. The caller is
 * also responsible for clearing any subsystem-owned state (framework cache,
 * kanban board cache, scheduler states, memory hashes, etc.) — those Maps
 * still live in manager-worker.ts and will move into their owning module in
 * the stacked follow-up PRs.
 *
 * Behavior-preserving against the prior in-file `clearAgentCaches`:
 * - `persistentSessionAgents` is intentionally NOT cleared here. The old
 *   implementation never deleted from it; preserve that.
 */
export function clearAgentState(agentId: string, codeName: string): boolean {
  // agentId-keyed
  agentState.knownVersions.delete(agentId);
  agentState.knownStatuses.delete(agentId);
  agentState.knownChannels.delete(agentId);
  agentState.writtenHashes.delete(agentId);
  agentState.knownSecretsHashes.delete(agentId);
  agentState.knownModels.delete(agentId);
  agentState.knownTasksHashes.delete(agentId);
  agentState.knownIntegrationHashes.delete(agentId);
  agentState.knownManagedMcpHashes.delete(agentId);
  agentState.knownManagedMcpStructure.delete(agentId);
  agentState.knownSenderPolicyHashes.delete(agentId);
  agentState.knownMsTeamsBehaviourHashes.delete(agentId);
  agentState.knownSlackBehaviourHashes.delete(agentId);
  agentState.knownGlobalSkillIds.delete(agentId);

  // codeName-keyed
  agentState.agentDisplayNames.delete(codeName);
  agentState.codeNameToAgentId.delete(codeName);

  // Compound-keyed (agentId:suffix)
  let channelCacheMutated = false;
  for (const key of agentState.knownChannelConfigHashes.keys()) {
    if (key.startsWith(`${agentId}:`)) {
      agentState.knownChannelConfigHashes.delete(key);
      channelCacheMutated = true;
    }
  }
  for (const key of agentState.knownSkillHashes.keys()) {
    if (key.startsWith(`${agentId}:`)) {
      agentState.knownSkillHashes.delete(key);
    }
  }

  return channelCacheMutated;
}
